import { observable, action, runInAction, computed } from 'mobx';
import authStore from './authStore';

export default class DataStore {
  @observable state = 'loading';
  @observable contacts = [];
  @observable vouchers = [];
  @observable deals = [];
  @observable recentCompletedDeals = [];
  @observable topOpenDeals = [];
  @observable transactionCategories = [];
  @observable payments = [];
  @observable accounts = [];
  @observable reports = [];

  @action
  reset() {
    this.state = 'loading';
    this.contacts = [];
    this.vouchers = [];
    this.deals = [];
    this.recentCompletedDeals = [];
    this.topOpenDeals = [];
    this.transactionCategories = [];
    this.payments = [];
    this.accounts = [];
    this.reports = [];
  }

  @action
  refreshDataAfterDealMap() {
    this.retrieveDeals();
    this.retrieveAllVouchers();
  }

  @computed
  get dealsArray() {
    return this.deals;
  }

  @computed
  get contactArray() {
    return this.contacts;
  }

  @computed
  get voucherArray() {
    return this.vouchers;
  }

  @computed
  get paymentArray() {
    return this.payments;
  }

  @computed
  get transactionCategoryArray() {
    return this.transactionCategories;
  }

  @computed
  get accountArray() {
    return this.accounts;
  }

  @computed
  get validAccountArray() {
    return this.accounts.filter(e => e.StatusId);
  }

  @computed
  get reportArray() {
    return this.reports;
  }

  @computed
  get validTransactionCategoryArray() {
    return this.transactionCategories.filter(e => e.StatusId);
  }

  @computed
  get recentCompletedDealsArray() {
    return this.recentCompletedDeals;
  }

  @computed
  get topOpenDealsArray() {
    return this.topOpenDeals;
  }

  getContactFromId = id => {
    return this.contacts.find(e => e.ContactId === id);
  };

  getContactFromName = name => {
    return this.contacts.find(e => e.Name === name);
  };

  getVoucherFromId = id => {
    return this.vouchers.find(e => e.VoucherId === id);
  };

  getPaymentFromId = id => {
    return this.payments.find(e => e.PaymentId === id);
  };

  getTranCategoryFromId = id => {
    return this.transactionCategories.find(e => e.TranCategoryId === id);
  };

  getAccountFromId = id => {
    return this.accounts.find(e => e.AccountId === id);
  };

  getReportFromId = id => {
    return this.reports.find(e => e.TemplateId === id);
  };

  async retrieveDeals() {
    try {
      const data = await this.fetchDeals();
      runInAction(() => {
        this.deals = data;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async retrieveContacts() {
    try {
      const data = await this.fetchContacts();
      runInAction(() => {
        this.contacts = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async retrieveAllVouchers() {
    try {
      const data = await this.fetchAllVouchers();
      runInAction(() => {
        this.vouchers = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async retrieveFilteredVouchers(filterJson) {
    try {
      const data = await this.fetchFilteredVouchers(filterJson);
      runInAction(() => {
        this.vouchers = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async retrieveAllPayments() {
    try {
      const data = await this.fetchAllPayments();
      runInAction(() => {
        this.payments = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async retrieveFilteredPayments(filterJson) {
    try {
      const data = await this.fetchFilteredPayments(filterJson);
      runInAction(() => {
        this.payments = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async retrieveAccounts() {
    try {
      const data = await this.fetchAccounts();
      runInAction(() => {
        this.accounts = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async retrieveReports() {
    try {
      const data = await this.fetchReports();
      runInAction(() => {
        this.reports = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async retrieveTransactionCategories() {
    try {
      const data = await this.fetchTransactionCategories();
      runInAction(() => {
        this.transactionCategories = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async retrieveRecentCompletedDeals() {
    try {
      const data = await this.fetchRecentCompletedDeals();
      runInAction(() => {
        this.recentCompletedDeals = data;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async retrieveTopOpenDeals() {
    try {
      const data = await this.fetchTopOpenDeals();
      runInAction(() => {
        this.topOpenDeals = data;
      });
    } catch (e) {
      console.error(e);
    }
  }

  fetchDeals = async () => {
    const { data } = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/deals`,
    );

    return data;
  };

  fetchContacts = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/contacts`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchAllVouchers = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/vouchers`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchFilteredVouchers = async filterJson => {
    try {
      const {
        data,
      } = await authStore.request.post(
        `${process.env.REACT_APP_API}/api/vouchers`,
        { filterJson },
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchAllPayments = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/payments`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchFilteredPayments = async filterJson => {
    try {
      const {
        data,
      } = await authStore.request.post(
        `${process.env.REACT_APP_API}/api/payments`,
        { filterJson },
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchTransactionCategories = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/transaction-categories`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchAccounts = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/accounts`,
      );

      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchReports = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/report-templates`,
      );

      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchRecentCompletedDeals = async () => {
    const { data } = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/recent-completed-deals`,
    );

    return data;
  };

  fetchTopOpenDeals = async () => {
    const { data } = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/top-open-deals`,
    );

    return data;
  };
}
